import React from "react";
import { Box, Tooltip } from "@mui/material";
import { oklchToCSS } from "../../oklab";

// A color filling a square, in the space it was measured in. The patch's average color is held in
// OKLCh, the space the analysis measures in, so the swatch shows the very numbers the color search
// ranks on; what a calibration reads off a picture is plain 0..255 RGB, and is shown as such. An RGB
// square can be split along its diagonal to set a second color against the first: the top-left
// half is rgb, the bottom-right half splitRgb.
type ColorSampleProps =
  | {
      l: number;
      c: number;
      h: number;
      rgb?: undefined;
      splitRgb?: undefined;
      size: number;
    }
  | {
      rgb?: number[];
      splitRgb?: number[];
      l?: undefined;
      c?: undefined;
      h?: undefined;
      size: number;
    };

const rgbText = ([red, green, blue]: number[]) =>
  `R ${red} · G ${green} · B ${blue}`;
const rgbCSS = ([red, green, blue]: number[]) =>
  `rgb(${red}, ${green}, ${blue})`;

const ColorSample = ({ l, c, h, rgb, splitRgb, size }: ColorSampleProps) => {
  let title: React.ReactNode;
  let background: string;
  if (rgb !== undefined) {
    if (rgb.length !== 3) return null;
    if (splitRgb !== undefined && splitRgb.length === 3) {
      title = (
        <>
          {rgbText(rgb)}
          <br />
          {rgbText(splitRgb)}
        </>
      );
      background = `linear-gradient(to bottom right, ${rgbCSS(rgb)} 50%, ${rgbCSS(splitRgb)} 50%)`;
    } else {
      title = rgbText(rgb);
      background = rgbCSS(rgb);
    }
  } else {
    if (l == null || c == null || h == null) return null;
    title = `L ${l.toFixed(3)} · C ${c.toFixed(3)} · h ${h.toFixed(1)}°`;
    background = oklchToCSS({ l, c, h });
  }

  return (
    <Tooltip placement="top" title={title}>
      <Box sx={{ width: size, height: size, background }} />
    </Tooltip>
  );
};

export default ColorSample;
